import { lazy, Suspense, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  InventoryBreakdownRow,
  InventoryReport,
  InventoryReportQuery,
  ProductTypeQuantity,
  StockAgeingBucket,
} from '@optiflow/types';
import { Card } from '@/components/ui/Card';
import { formatMoney } from '@/modules/catalog/money';
import { useLocale } from '@/hooks/useLocale';
import { InventoryRecords, type InventoryRecordsRequest } from './InventoryRecords';

const InventoryCountChart = lazy(() => import('./InventoryCountChart'));

/** The calendar tags the reports use for dates: en-GB, and ar-EG for Arabic. */
const LOCALE_TAG = { en: 'en-GB', ar: 'ar-EG' } as const;

const BUCKETS: StockAgeingBucket[] = [
  'RECENT',
  'SLOW',
  'DEAD',
  'UNKNOWN_HISTORY',
  'BELOW_AGEING_MINIMUM',
];

/** Quantities by product type, one per line: a frame and a box of lenses are never added together. */
function TypeQuantities({ rows, signed }: { rows: ProductTypeQuantity[]; signed?: boolean }) {
  const { t } = useTranslation();
  if (rows.length === 0) return <span className="text-muted">—</span>;
  return (
    <span className="flex flex-col">
      {rows.map((row) => (
        <span key={row.productType ?? 'unknown'} className="whitespace-nowrap">
          <span className="text-muted text-xs">
            {row.productType
              ? t(`catalog.types.${row.productType}`)
              : t('reports.sales.unattributed')}
          </span>{' '}
          <span dir="ltr" className="tabular-nums">
            {signed && row.quantity > 0 ? `+${row.quantity}` : row.quantity}
          </span>
        </span>
      ))}
    </span>
  );
}

function Stat({
  label,
  children,
  hint,
  action,
}: {
  label: string;
  children: ReactNode;
  hint?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <Card className="min-w-0 p-4">
      <p className="text-muted text-sm">{label}</p>
      <div className="text-ink mt-2 text-2xl font-semibold">{children}</div>
      {hint ? <div className="text-muted mt-1 text-xs">{hint}</div> : null}
      {action}
    </Card>
  );
}

function Section({
  id,
  title,
  note,
  children,
}: {
  id: string;
  title: string;
  note?: string;
  children: ReactNode;
}) {
  return (
    <section aria-labelledby={id} className="space-y-3">
      <div>
        <h2 id={id} className="text-ink font-semibold">
          {title}
        </h2>
        {note ? <p className="text-muted text-xs">{note}</p> : null}
      </div>
      {children}
    </section>
  );
}

/**
 * The Inventory tab.
 *
 * Current stock — lines, units, value, alerts, ageing, in transit — is as of now, whatever period is
 * chosen, and is never compared with a previous period. The period narrows movements, transfers,
 * counts and adjustments only. Every chart has a table, and every figure opens into its records.
 */
export function InventoryContent({
  report,
  query,
  onSelectVariant,
}: {
  report: InventoryReport;
  query: InventoryReportQuery;
  /** Narrows the whole tab to one item; offered from a drilldown row. */
  onSelectVariant?: (variantId: string) => void;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const [open, setOpen] = useState<InventoryRecordsRequest | null>(null);
  const { snapshot, value } = report;
  const base = report.meta.baseCurrencyCode;
  const count = (value: number) => <span dir="ltr">{value}</span>;
  // Buckets read as dates in the reader's own calendar language, as the Overview trend does.
  const dateFormat = new Intl.DateTimeFormat(LOCALE_TAG[locale], {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
  const bucketLabel = (bucket: { from: string; to: string }) => {
    const from = dateFormat.format(new Date(`${bucket.from}T00:00:00Z`));
    return bucket.from === bucket.to
      ? from
      : `${from} – ${dateFormat.format(new Date(`${bucket.to}T00:00:00Z`))}`;
  };

  const view = (request: InventoryRecordsRequest) => (
    <button
      type="button"
      onClick={() => setOpen(request)}
      className="text-brand-700 mt-2 text-sm font-medium hover:underline"
      aria-label={t('reports.sales.records.viewFor', { figure: request.title })}
    >
      {t('reports.sales.records.view')}
    </button>
  );

  const records = open ? (
    <InventoryRecords
      key={`${open.kind}-${open.health ?? ''}`}
      query={query}
      request={open}
      baseCurrencyCode={base}
      onSelectVariant={
        onSelectVariant
          ? (variantId) => {
              setOpen(null);
              onSelectVariant(variantId);
            }
          : undefined
      }
      onClose={() => setOpen(null)}
    />
  ) : null;

  // A grid item must be allowed to shrink (min-w-0) so its table scrolls inside the card, not the page.
  const breakdown = (title: string, rows: InventoryBreakdownRow[], note?: string) => (
    <Card className="min-w-0 p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h3 className="text-ink font-semibold">{title}</h3>
        {note ? <p className="text-muted text-xs">{note}</p> : null}
      </div>
      {rows.length === 0 ? (
        <p className="text-muted mt-3 text-sm">{t('reports.inventory.empty')}</p>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[30rem] text-sm">
            <thead>
              <tr className="text-muted border-line border-b">
                <th className="py-2 text-start font-medium">{t('reports.sales.columns.name')}</th>
                <th className="py-2 text-end font-medium">
                  {t('reports.inventory.columns.lines')}
                </th>
                <th className="py-2 text-end font-medium">{t('reports.inventory.columns.low')}</th>
                <th className="py-2 text-end font-medium">{t('reports.inventory.columns.out')}</th>
                <th className="py-2 text-end font-medium">
                  {t('reports.inventory.columns.slowDead')}
                </th>
                <th className="py-2 text-end font-medium">
                  {t('reports.inventory.columns.units')}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id ?? 'unattributed'} className="border-line border-b last:border-b-0">
                  <td className="py-2">
                    {row.label ?? (
                      <span className="text-muted italic">{t('reports.sales.unattributed')}</span>
                    )}
                  </td>
                  <td className="py-2 text-end tabular-nums">{count(row.onHandLines)}</td>
                  <td className="py-2 text-end tabular-nums">{count(row.lowStockLines)}</td>
                  <td className="py-2 text-end tabular-nums">{count(row.outOfStockLines)}</td>
                  <td className="py-2 text-end tabular-nums">
                    <span dir="ltr">
                      {row.slowMovingLines} / {row.deadStockLines}
                    </span>
                  </td>
                  <td className="py-2 text-end">
                    <TypeQuantities rows={row.units} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );

  return (
    <div className="space-y-6">
      <Section
        id="inventory-now"
        title={t('reports.inventory.sections.now')}
        note={t('reports.inventory.snapshotNote')}
      >
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <Stat
            label={t('reports.inventory.kpis.onHandLines')}
            hint={t('reports.inventory.hints.variants', { count: snapshot.onHandVariants })}
          >
            {count(snapshot.onHandLines)}
          </Stat>
          <Stat
            label={t('reports.inventory.kpis.unitsOnHand')}
            hint={t('reports.inventory.hints.perType')}
          >
            <span className="text-base font-medium">
              <TypeQuantities rows={snapshot.unitsOnHand} />
            </span>
          </Stat>
          {value ? (
            <Stat
              label={t('reports.kpis.estimatedValue')}
              hint={
                <>
                  {value.partialEstimate ? (
                    <span className="me-2 rounded bg-amber-50 px-1.5 py-0.5 font-medium text-amber-800">
                      {t('reports.partialEstimate')}
                    </span>
                  ) : null}
                  {value.lineCoverageBasisPoints === null
                    ? t('reports.coverage.noStock')
                    : t('reports.coverage.lines', {
                        percent: (value.lineCoverageBasisPoints / 100).toFixed(1),
                        costed: value.costedLineCount,
                        total: value.totalOnHandLineCount,
                      })}
                </>
              }
              action={view({
                kind: 'STOCK_LINES',
                health: 'COSTED',
                title: t('reports.inventory.records.costed'),
              })}
            >
              <span dir="ltr">
                {formatMoney(value.estimatedValue[0]?.amountMinor ?? 0, locale, base)}
              </span>
            </Stat>
          ) : null}
          <Stat
            label={t('reports.inventory.kpis.lowStock')}
            action={view({
              kind: 'STOCK_LINES',
              health: 'LOW_STOCK',
              title: t('reports.inventory.kpis.lowStock'),
            })}
          >
            {count(snapshot.lowStockLines)}
          </Stat>
          <Stat
            label={t('reports.inventory.kpis.outOfStock')}
            action={view({
              kind: 'STOCK_LINES',
              health: 'OUT_OF_STOCK',
              title: t('reports.inventory.kpis.outOfStock'),
            })}
          >
            {count(snapshot.outOfStockLines)}
          </Stat>
          <Stat
            label={t('reports.inventory.kpis.inTransit')}
            hint={t('reports.inventory.hints.inTransit', { count: report.inTransit.openTransfers })}
            action={view({ kind: 'IN_TRANSIT', title: t('reports.inventory.kpis.inTransit') })}
          >
            <span className="text-base font-medium">
              <TypeQuantities rows={report.inTransit.units} />
            </span>
          </Stat>
        </div>
        {records &&
        open &&
        (open.kind === 'STOCK_LINES' || open.kind === 'IN_TRANSIT') &&
        !BUCKETS.includes(open.health as StockAgeingBucket)
          ? records
          : null}
      </Section>

      <Section
        id="inventory-ageing"
        title={t('reports.inventory.sections.ageing')}
        note={t('reports.inventory.ageingNote', {
          slow: snapshot.slowMovingDays,
          dead: snapshot.deadStockDays,
          minimum: snapshot.minimumOnHandForSlowMoving,
        })}
      >
        <Card className="p-4">
          <Suspense fallback={<div className="h-48" aria-hidden />}>
            <InventoryCountChart
              label={t('reports.inventory.sections.ageing')}
              data={BUCKETS.map((bucket) => ({
                label: t(`reports.inventory.ageing.${bucket}`),
                value: snapshot.ageing[bucket],
              }))}
            />
          </Suspense>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-muted border-line border-b">
                  <th className="py-2 text-start font-medium">
                    {t('reports.inventory.columns.bucket')}
                  </th>
                  <th className="py-2 text-end font-medium">
                    {t('reports.inventory.columns.lines')}
                  </th>
                  <th className="py-2 text-end font-medium" />
                </tr>
              </thead>
              <tbody>
                {BUCKETS.map((bucket) => (
                  <tr key={bucket} className="border-line border-b last:border-b-0">
                    <td className="py-2">
                      <span className="text-ink">{t(`reports.inventory.ageing.${bucket}`)}</span>
                      <span className="text-muted block text-xs">
                        {t(`reports.inventory.ageingHint.${bucket}`, {
                          slow: snapshot.slowMovingDays,
                          dead: snapshot.deadStockDays,
                        })}
                      </span>
                    </td>
                    <td className="py-2 text-end tabular-nums">{count(snapshot.ageing[bucket])}</td>
                    <td className="py-2 text-end">
                      {view({
                        kind: 'STOCK_LINES',
                        health: bucket,
                        title: t(`reports.inventory.ageing.${bucket}`),
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
        {records &&
        open &&
        open.kind === 'STOCK_LINES' &&
        BUCKETS.includes(open.health as StockAgeingBucket)
          ? records
          : null}
      </Section>

      {value ? (
        <Section
          id="inventory-value"
          title={t('reports.inventory.sections.value')}
          note={t('reports.inventory.valueNote')}
        >
          <Card className="p-4">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[28rem] text-sm">
                <thead>
                  <tr className="text-muted border-line border-b">
                    <th className="py-2 text-start font-medium">
                      {t('reports.inventory.columns.type')}
                    </th>
                    <th className="py-2 text-end font-medium">
                      {t('reports.inventory.columns.costedLines')}
                    </th>
                    <th className="py-2 text-end font-medium">
                      {t('reports.inventory.columns.costedUnits')}
                    </th>
                    <th className="py-2 text-end font-medium">
                      {t('reports.inventory.columns.unitCoverage')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {value.byProductType.map((row) => (
                    <tr
                      key={row.productType ?? 'unknown'}
                      className="border-line border-b last:border-b-0"
                    >
                      <td className="py-2">
                        {row.productType
                          ? t(`catalog.types.${row.productType}`)
                          : t('reports.sales.unattributed')}
                      </td>
                      <td className="py-2 text-end tabular-nums" dir="ltr">
                        {row.costedLines} / {row.onHandLines}
                      </td>
                      <td className="py-2 text-end tabular-nums" dir="ltr">
                        {row.costedUnits} / {row.onHandUnits}
                      </td>
                      <td className="py-2 text-end tabular-nums" dir="ltr">
                        {row.unitCoverageBasisPoints === null
                          ? '—'
                          : `${(row.unitCoverageBasisPoints / 100).toFixed(1)}%`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {view({
              kind: 'STOCK_LINES',
              health: 'MISSING_COST',
              title: t('reports.inventory.records.missingCost'),
            })}
          </Card>
          {records && open && open.health === 'MISSING_COST' ? records : null}
        </Section>
      ) : null}

      <Section id="inventory-breakdowns" title={t('reports.inventory.sections.breakdowns')}>
        <div className="grid gap-4 xl:grid-cols-2">
          {breakdown(t('reports.inventory.byBranch'), report.byBranch)}
          {breakdown(
            t('reports.inventory.byCategory'),
            report.byCategory,
            t('reports.sales.hints.currentCatalogue'),
          )}
          {breakdown(
            t('reports.inventory.byBrand'),
            report.byBrand,
            t('reports.sales.hints.currentCatalogue'),
          )}
        </div>
      </Section>

      <Section
        id="inventory-movements"
        title={t('reports.inventory.sections.movements')}
        note={t('reports.inventory.periodNote')}
      >
        <Card className="p-4">
          <p className="text-muted text-sm">
            {t('reports.inventory.kpis.movements')}:{' '}
            <span className="text-ink font-semibold">{count(report.movements.movements)}</span>
          </p>
          <Suspense fallback={<div className="h-48" aria-hidden />}>
            <InventoryCountChart
              label={t('reports.inventory.sections.movements')}
              data={report.movements.trend.buckets.map((bucket, index) => ({
                label: bucketLabel(bucket),
                value: report.movements.trend.movements[index] ?? 0,
              }))}
            />
          </Suspense>
          {report.movements.byCategory.length === 0 ? (
            <p className="text-muted mt-3 text-sm">{t('reports.inventory.noMovements')}</p>
          ) : (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full min-w-[26rem] text-sm">
                <thead>
                  <tr className="text-muted border-line border-b">
                    <th className="py-2 text-start font-medium">
                      {t('reports.inventory.columns.movement')}
                    </th>
                    <th className="py-2 text-end font-medium">
                      {t('reports.inventory.columns.count')}
                    </th>
                    <th className="py-2 text-end font-medium">
                      {t('reports.inventory.columns.quantity')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {report.movements.byCategory.map((row) => (
                    <tr key={row.category} className="border-line border-b last:border-b-0">
                      <td className="py-2">{t(`reports.inventory.movement.${row.category}`)}</td>
                      <td className="py-2 text-end tabular-nums">{count(row.movements)}</td>
                      <td className="py-2 text-end">
                        <TypeQuantities rows={row.units} signed />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {view({ kind: 'MOVEMENTS', title: t('reports.inventory.sections.movements') })}
        </Card>
        {records && open && open.kind === 'MOVEMENTS' ? records : null}
      </Section>

      <Section
        id="inventory-transfers"
        title={t('reports.inventory.sections.transfers')}
        note={t('reports.inventory.transfersNote')}
      >
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <Stat label={t('reports.inventory.kpis.transfersCreated')}>
            {count(report.transfers.created)}
          </Stat>
          <Stat
            label={t('reports.inventory.kpis.transfersDispatched')}
            hint={<TypeQuantities rows={report.transfers.dispatchedUnits} />}
          >
            {count(report.transfers.dispatched)}
          </Stat>
          <Stat
            label={t('reports.inventory.kpis.transfersCompleted')}
            hint={<TypeQuantities rows={report.transfers.receivedUnits} />}
          >
            {count(report.transfers.completed)}
          </Stat>
          <Stat
            label={t('reports.inventory.kpis.transferVariance')}
            hint={
              <>
                <TypeQuantities rows={report.transfers.variance.units} signed />
                <span className="mt-1 block">{t('reports.inventory.hints.varianceSign')}</span>
              </>
            }
            action={view({
              kind: 'TRANSFER_VARIANCE',
              title: t('reports.inventory.kpis.transferVariance'),
            })}
          >
            {count(report.transfers.variance.lines)}
          </Stat>
        </div>
        {records && open && open.kind === 'TRANSFER_VARIANCE' ? records : null}
      </Section>

      <Section
        id="inventory-stocktakes"
        title={t('reports.inventory.sections.stocktakes')}
        note={t('reports.inventory.stocktakeNote')}
      >
        <Card className="p-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <p className="text-sm">
              <span className="text-muted">{t('reports.inventory.kpis.stocktakesPosted')}: </span>
              <span className="font-semibold">{count(report.stocktakes.posted)}</span>
            </p>
            <p className="text-sm">
              <span className="text-muted">{t('reports.inventory.kpis.countedLines')}: </span>
              <span className="font-semibold">{count(report.stocktakes.countedLines)}</span>
            </p>
            <p className="text-sm">
              <span className="text-muted">{t('reports.inventory.kpis.affectedLines')}: </span>
              <span className="font-semibold">{count(report.stocktakes.affectedLines)}</span>
            </p>
          </div>
          {report.stocktakes.variance.length > 0 ? (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full min-w-[26rem] text-sm">
                <thead>
                  <tr className="text-muted border-line border-b">
                    <th className="py-2 text-start font-medium">
                      {t('reports.inventory.columns.type')}
                    </th>
                    <th className="py-2 text-end font-medium">
                      {t('reports.inventory.columns.increase')}
                    </th>
                    <th className="py-2 text-end font-medium">
                      {t('reports.inventory.columns.decrease')}
                    </th>
                    <th className="py-2 text-end font-medium">
                      {t('reports.inventory.columns.net')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {report.stocktakes.variance.map((row) => (
                    <tr
                      key={row.productType ?? 'unknown'}
                      className="border-line border-b last:border-b-0"
                    >
                      <td className="py-2">
                        {row.productType
                          ? t(`catalog.types.${row.productType}`)
                          : t('reports.sales.unattributed')}
                      </td>
                      <td className="py-2 text-end tabular-nums">{count(row.increase)}</td>
                      <td className="py-2 text-end tabular-nums">{count(row.decrease)}</td>
                      <td className="py-2 text-end tabular-nums">
                        <span dir="ltr">{row.net > 0 ? `+${row.net}` : row.net}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-muted mt-3 text-sm">{t('reports.inventory.noStocktakes')}</p>
          )}
          {view({ kind: 'STOCKTAKE_LINES', title: t('reports.inventory.sections.stocktakes') })}
        </Card>
        {records && open && open.kind === 'STOCKTAKE_LINES' ? records : null}
      </Section>

      <Section id="inventory-adjustments" title={t('reports.inventory.sections.adjustments')}>
        <Card className="p-4">
          <p className="text-muted text-sm">
            {t('reports.inventory.kpis.adjustments')}:{' '}
            <span className="text-ink font-semibold">{count(report.adjustments.movements)}</span>
          </p>
          {report.adjustments.byReasonType.length > 0 ? (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full min-w-[26rem] text-sm">
                <thead>
                  <tr className="text-muted border-line border-b">
                    <th className="py-2 text-start font-medium">
                      {t('reports.inventory.columns.reason')}
                    </th>
                    <th className="py-2 text-end font-medium">
                      {t('reports.inventory.columns.count')}
                    </th>
                    <th className="py-2 text-end font-medium">
                      {t('reports.inventory.columns.increase')}
                    </th>
                    <th className="py-2 text-end font-medium">
                      {t('reports.inventory.columns.decrease')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {report.adjustments.byReasonType.map((row) => (
                    <tr
                      key={row.reasonType ?? 'none'}
                      className="border-line border-b last:border-b-0"
                    >
                      <td className="py-2">
                        {row.reasonType
                          ? t(`reports.inventory.reason.${row.reasonType}`)
                          : t('reports.sales.unattributed')}
                      </td>
                      <td className="py-2 text-end tabular-nums">{count(row.movements)}</td>
                      <td className="py-2 text-end">
                        <TypeQuantities
                          rows={row.units
                            .filter((unit) => unit.increase > 0)
                            .map((unit) => ({
                              productType: unit.productType,
                              quantity: unit.increase,
                            }))}
                        />
                      </td>
                      <td className="py-2 text-end">
                        <TypeQuantities
                          rows={row.units
                            .filter((unit) => unit.decrease > 0)
                            .map((unit) => ({
                              productType: unit.productType,
                              quantity: unit.decrease,
                            }))}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-muted mt-3 text-sm">{t('reports.inventory.noAdjustments')}</p>
          )}
        </Card>
      </Section>

      {report.zeroSales ? (
        <Section
          id="inventory-zero-sales"
          title={t('reports.inventory.sections.zeroSales')}
          note={t('reports.inventory.zeroSalesNote')}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Stat
              label={t('reports.inventory.kpis.zeroSalesLines')}
              hint={<TypeQuantities rows={report.zeroSales.units} />}
              action={view({
                kind: 'ZERO_SALES',
                title: t('reports.inventory.sections.zeroSales'),
              })}
            >
              {count(report.zeroSales.lines)}
            </Stat>
          </div>
          {records && open && open.kind === 'ZERO_SALES' ? records : null}
        </Section>
      ) : null}

      <p className="text-muted text-xs">{t('reports.inventory.disclosure')}</p>
    </div>
  );
}
